// pages/ProfStudentsPage.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/axiosInstance";

const ProfStudentsPage = () => {
  const { user } = useAuth();
  const profId = user?.id;

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profId) return;

    const fetchStudents = async () => {
      try {
        const res = await api.get(`/courses/students/${profId}`);
        setStudents(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [profId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-5">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-600 text-lg font-medium">No students enrolled in your courses yet.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">

      {/* Desktop Table */}
      <div className="hidden mt-6 lg:block overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-4 px-6 text-left font-semibold text-gray-700">Student Name</th>
                <th className="py-4 px-6 text-left font-semibold text-gray-700">Email</th>
                <th className="py-4 px-6 text-left font-semibold text-gray-700">Course Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map((s) => (
                <tr 
                  key={`${s.student_id}-${s.course_id}`} 
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="py-4 px-6 font-medium text-gray-900">{s.student_name}</td>
                  <td className="py-4 px-6 text-gray-700">
                    <a 
                      href={`mailto:${s.student_email}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      {s.student_email}
                    </a>
                  </td>
                  <td className="py-4 px-6">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {s.course_name}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {students.map((s) => (
          <div 
            key={`${s.student_id}-${s.course_id}-mobile`}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold text-lg text-gray-900">{s.student_name}</h3>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-800">
                {s.course_name}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center text-gray-700">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a 
                  href={`mailto:${s.student_email}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline text-sm truncate"
                >
                  {s.student_email}
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfStudentsPage;